import { toast } from "react-toastify";
import useLeagueContext from "../../utils/useLeagueContext";
import {
  isEligibleInLeague,
  tradeSymbolOf,
} from "../../utils/leagueAssets";
import { chartSymbolFor } from "./watchlistFormat";

/**
 * "Can this watchlist row enter the Trade flow from where I'm standing?"
 *
 * Client, "League Watchlist: launch behavior":
 *
 *   "The League Watchlist screen still shows Watchlist A with all 10 assets…
 *    Nothing is visually filtered out of the watchlist itself… The 5 ineligible
 *    assets stay visible in Watchlist A, but they simply cannot enter the league
 *    Trade flow. If you tap one of those ineligible assets from the Watchlist
 *    while inside the league, show Not eligible in this league."
 *
 * So this hook deliberately changes NOTHING about what a list contains or how it
 * renders. It only answers what happens when a row is tapped, and it answers
 * differently in three places:
 *
 *   • on the main site — untouched. The row opens /chartSearch/<symbol>, exactly
 *     as it always has.
 *   • inside a league, eligible — the row opens the LEAGUE's Trade screen rather
 *     than walking the member back out to the main chart. The client's phrase is
 *     "block entry into the league Trade flow", which only reads as a block if
 *     an eligible row is entering that flow in the first place.
 *   • inside a league, ineligible — nothing opens, and the row says why.
 *
 * Called ONCE per table (AssetTable, MobileListDetail) and handed down, rather
 * than once per row: useLeagueContext registers a listener per call site, and a
 * fifty-row list does not need fifty of them.
 */
export default function useLeagueAssetGate() {
  const { slug, game, isMember } = useLeagueContext();

  // A visitor previewing a league is not in it and keeps the main app's
  // behaviour, the same rule the rest of the shell uses.
  const league = slug && isMember && game ? game : null;

  const symbolFor = (item) => chartSymbolFor(item);

  // Where a row should go, or null when it must not go anywhere.
  const linkFor = (item) => {
    if (!league) return `/chartSearch/${symbolFor(item)}`;
    if (!isEligibleInLeague(league, tradeSymbolOf(item))) return null;
    return `/${slug}/trade`;
  };

  /**
   * Run on tap. Returns true when the caller should let the navigation happen.
   *
   * The ticker is written to localStorage the way every other watchlist row has
   * always written it — the chart and the order ticket both read it from there.
   */
  const onOpen = (item) => {
    // ELIGIBILITY ONLY — deliberately not the league's start date. A league that
    // has not opened yet still lets its members read the chart; what it refuses
    // is an order, and that refusal lives on the order ticket (and, finally, on
    // the order endpoint). Blocking a chart here would take away something the
    // client never asked us to take away.
    if (league && !isEligibleInLeague(league, tradeSymbolOf(item))) {
      toast.error("This asset is not eligible for this league.");
      return false;
    }

    localStorage.setItem("ticker", symbolFor(item));
    return true;
  };

  return { league, linkFor, onOpen };
}
